import aiAPI from '@edulastic/api/src/aiAPI'
import { testItemsApi, segmentApi } from '@edulastic/api'
import { get, uniq, chunk, omit } from 'lodash'
import {
  all,
  call,
  put,
  select,
  take,
  takeLatest,
  takeEvery,
} from 'redux-saga/effects'
import { createSelector } from 'reselect'
import { notification } from '@edulastic/common'
import { statusConstants } from '@edulastic/constants/const/test'
import {
  buildQuestionForBulkSave,
  buildQuestionFromPersistedFields,
  buildRemapItemIdMap,
  findItemOnTest,
  getEligibleTestMetadataQuestions,
  getItemQuestionIds,
  getUpdatedQuestionIds,
  hasBulkSuggestion,
} from '../utils/AIQuestionAssistance'
import {
  getTestItemsSelector,
  getTestEntitySelector,
  receiveTestByIdAction,
  removeCreatedItemsByIdAction,
  RECEIVE_TEST_BY_ID_SUCCESS,
} from '../../TestPage/ducks'
import { getCurriculumsListSelector } from '../../src/selectors/dictionaries'
import {
  getUserIdSelector,
  getCollectionsSelector,
  getWritableCollectionsSelector,
  getUserRole,
  getIsCurator,
} from '../../src/selectors/user'
import {
  withSelectedRubricOnSuggestion,
  resolveRubricForApply,
} from '../utils/rubricApply'

export const namespaceKey = 'aiBulkQuestionMetadata'

export const BULK_METADATA_BATCH_SIZE = 20

export const BULK_ITEM_STATUS = {
  GENERATING: 'GENERATING',
  SUCCESS: 'SUCCESS',
  SAVING: 'SAVING',
  ERROR: 'ERROR',
}

// Denormalized/read-only fields on test.itemGroups items not allowed by PUT /testitem Joi schema.
const TEST_ITEM_SAVE_FIELDS_TO_OMIT = [
  '_id',
  '__v',
  'autoGrade',
  'previousTestItemId',
  'public',
  'analytics',
  'algoVariablesEnabled',
  'sharedType',
  'sharedWith',
  'createdAt',
  'updatedAt',
  'passageContent',
  'passageTitle',
  'passageLexileValue',
  'passageItemsCount',
  'passageFleschKincaid',
  'passageSource',
  'previousTestItemId',
  'passageData',
  'groupId',
  'isLimitedDeliveryType',
  'groupPassageId',
]

const buildUpdatedItemForSave = (item, updatedQuestions) => ({
  ...omit(item, TEST_ITEM_SAVE_FIELDS_TO_OMIT),
  data: { ...item.data, questions: updatedQuestions },
})

/**
 * Saved items (and their cloned/versioned ids) are persisted in the test, so
 * their createdItems snapshots are stale. Dropping them before refetching the
 * test keeps the latest DB version in redux; other unsaved items are kept.
 */
const getSavedItemIds = (succeeded = [], itemIdMap = {}) =>
  uniq([...succeeded, ...succeeded.map((id) => itemIdMap[id]).filter(Boolean)])

// action constants
export const SET_BULK_STANDARDS_SELECTION = `[${namespaceKey}] set selection`
export const GENERATE_BULK_STANDARDS = `[${namespaceKey}] generate`
export const SET_BULK_STANDARDS_STATE = `[${namespaceKey}] set state`
export const SET_BULK_ITEM_STATUS = `[${namespaceKey}] set item status`
export const SET_BULK_SUGGESTIONS = `[${namespaceKey}] set suggestions`
export const UPDATE_BULK_SUGGESTION = `[${namespaceKey}] update suggestion`
export const SAVE_ITEM_STANDARD = `[${namespaceKey}] save item standard`
export const SAVE_ALL_ITEM_STANDARDS = `[${namespaceKey}] save all item standards`
export const CLEAR_BULK_ITEM = `[${namespaceKey}] clear item`
export const RESET_BULK_STANDARDS_STATE = `[${namespaceKey}] reset state`
export const REMAP_BULK_ITEM_IDS = `[${namespaceKey}] remap bulk item ids`
export const INIT_EXISTING_METADATA_EDIT = `[${namespaceKey}] init existing metadata edit`
export const SET_EXISTING_METADATA_EDIT = `[${namespaceKey}] set existing metadata edit`
export const UPDATE_EXISTING_METADATA_EDIT = `[${namespaceKey}] update existing metadata edit`
export const CLEAR_EXISTING_METADATA_EDIT = `[${namespaceKey}] clear existing metadata edit`

// actions
export const setBulkStandardsSelectionAction = (payload) => ({
  type: SET_BULK_STANDARDS_SELECTION,
  payload,
})

export const generateBulkStandardsAction = (payload) => ({
  type: GENERATE_BULK_STANDARDS,
  payload,
})

export const setBulkStandardsStateAction = (payload) => ({
  type: SET_BULK_STANDARDS_STATE,
  payload,
})

export const setBulkItemStatusAction = (payload) => ({
  type: SET_BULK_ITEM_STATUS,
  payload,
})

export const setBulkSuggestionsAction = (payload) => ({
  type: SET_BULK_SUGGESTIONS,
  payload,
})

export const updateBulkSuggestionAction = (payload) => ({
  type: UPDATE_BULK_SUGGESTION,
  payload,
})

export const saveItemStandardAction = (payload) => ({
  type: SAVE_ITEM_STANDARD,
  payload,
})

export const saveAllItemStandardsAction = () => ({
  type: SAVE_ALL_ITEM_STANDARDS,
})

export const clearBulkItemAction = (payload) => ({
  type: CLEAR_BULK_ITEM,
  payload,
})

export const resetBulkStandardsStateAction = () => ({
  type: RESET_BULK_STANDARDS_STATE,
})

export const remapBulkItemIdsAction = (payload) => ({
  type: REMAP_BULK_ITEM_IDS,
  payload,
})

export const initExistingMetadataEditAction = (payload) => ({
  type: INIT_EXISTING_METADATA_EDIT,
  payload,
})

export const setExistingMetadataEditAction = (payload) => ({
  type: SET_EXISTING_METADATA_EDIT,
  payload,
})

export const updateExistingMetadataEditAction = (payload) => ({
  type: UPDATE_EXISTING_METADATA_EDIT,
  payload,
})

export const clearExistingMetadataEditAction = () => ({
  type: CLEAR_EXISTING_METADATA_EDIT,
})

// selectors
export const stateSelector = (state) => state?.[namespaceKey]

export const getBulkStandardsStateSelector = createSelector(
  stateSelector,
  (state) => state || {}
)

export const getBulkApiStatusSelector = createSelector(
  getBulkStandardsStateSelector,
  (state) => state?.apiStatus || null
)

export const getBulkItemStatusSelector = createSelector(
  getBulkStandardsStateSelector,
  (state) => state?.itemStatus || {}
)

export const getBulkSuggestionsSelector = createSelector(
  getBulkStandardsStateSelector,
  (state) => state?.suggestionsByQuestionId || {}
)

export const getExistingEditByQuestionIdSelector = createSelector(
  getBulkStandardsStateSelector,
  (state) => state?.existingEditByQuestionId || {}
)

export const getEditingItemIdSelector = createSelector(
  getBulkStandardsStateSelector,
  (state) => state?.editingItemId || null
)

export const getEligibleCountSelector = createSelector(
  getTestItemsSelector,
  getUserIdSelector,
  getCollectionsSelector,
  getWritableCollectionsSelector,
  getUserRole,
  getIsCurator,
  (
    testItems,
    userId,
    userCollections,
    writableCollections,
    userRole,
    isCurator
  ) =>
    getEligibleTestMetadataQuestions({
      testItems,
      userId,
      userCollections,
      writableCollections,
      userRole,
      isCurator,
    })?.length || 0
)

export const getPendingSuggestionsSelector = createSelector(
  getBulkItemStatusSelector,
  getBulkSuggestionsSelector,
  getTestItemsSelector,
  (itemStatus, suggestionsByQuestionId, testItems) =>
    (testItems || [])
      .map((item) => {
        const itemId = item?._id
        if (!itemId) return null
        const status = itemStatus[itemId]
        const questions = get(item, 'data.questions', [])
        const questionIds = questions
          .map((q) => q.id)
          .filter((qId) => hasBulkSuggestion(suggestionsByQuestionId[qId]))
        if (!questionIds.length) return null
        if (
          status &&
          status !== BULK_ITEM_STATUS.SUCCESS &&
          status !== BULK_ITEM_STATUS.SAVING
        ) {
          return null
        }
        return { itemId, questionIds }
      })
      .filter(Boolean)
)

// initial state
const initialState = {
  selectedGrade: null,
  selectedSubject: null,
  selectedCurriculumId: null,
  apiStatus: null,
  itemStatus: {},
  suggestionsByQuestionId: {},
  existingEditByQuestionId: {},
  editingItemId: null,
  errorMessage: null,
}

// reducer
export const reducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case SET_BULK_STANDARDS_SELECTION:
      return { ...state, ...payload }
    case SET_BULK_STANDARDS_STATE:
      return { ...state, ...payload }
    case SET_BULK_ITEM_STATUS: {
      const { itemIds = [], status } = payload || {}
      const itemStatus = { ...state.itemStatus }
      itemIds.forEach((id) => {
        itemStatus[id] = status
      })
      return { ...state, itemStatus }
    }
    case SET_BULK_SUGGESTIONS:
      return {
        ...state,
        suggestionsByQuestionId: {
          ...state.suggestionsByQuestionId,
          ...(payload || {}),
        },
      }
    case UPDATE_BULK_SUGGESTION: {
      const { questionId, updates = {} } = payload || {}
      if (!questionId) return state
      return {
        ...state,
        suggestionsByQuestionId: {
          ...state.suggestionsByQuestionId,
          [questionId]: {
            ...(state.suggestionsByQuestionId?.[questionId] || {}),
            ...updates,
          },
        },
      }
    }
    case CLEAR_BULK_ITEM: {
      const { itemId, questionIds = [], itemQuestionIds } = payload || {}
      const itemStatus = { ...state.itemStatus }
      const suggestionsByQuestionId = { ...state.suggestionsByQuestionId }
      questionIds.forEach((qId) => {
        delete suggestionsByQuestionId[qId]
      })

      const idsToCheck = itemQuestionIds || questionIds
      const hasRemainingSuggestions = idsToCheck.some(
        (qId) => suggestionsByQuestionId[qId] != null
      )
      if (!hasRemainingSuggestions) {
        delete itemStatus[itemId]
      } else {
        itemStatus[itemId] = BULK_ITEM_STATUS.SUCCESS
      }

      const apiStatus = Object.keys(itemStatus).length ? state.apiStatus : null
      return { ...state, itemStatus, suggestionsByQuestionId, apiStatus }
    }
    case REMAP_BULK_ITEM_IDS: {
      const { itemIdMap = {} } = payload || {}
      const entries = Object.entries(itemIdMap)
      if (!entries.length) return state

      const itemStatus = { ...state.itemStatus }
      const suggestionsByQuestionId = { ...state.suggestionsByQuestionId }

      entries.forEach(([oldItemId, mapping]) => {
        const newItemId =
          typeof mapping === 'string' ? mapping : mapping?.newItemId
        const questionIdMap =
          typeof mapping === 'object' && mapping?.questionIdMap
            ? mapping.questionIdMap
            : {}

        if (newItemId && itemStatus[oldItemId] !== undefined) {
          itemStatus[newItemId] = itemStatus[oldItemId]
          delete itemStatus[oldItemId]
        }

        Object.entries(questionIdMap).forEach(
          ([oldQuestionId, newQuestionId]) => {
            if (suggestionsByQuestionId[oldQuestionId] !== undefined) {
              suggestionsByQuestionId[newQuestionId] =
                suggestionsByQuestionId[oldQuestionId]
              delete suggestionsByQuestionId[oldQuestionId]
            }
          }
        )
      })

      return { ...state, itemStatus, suggestionsByQuestionId }
    }
    case SET_EXISTING_METADATA_EDIT: {
      const { existingEditByQuestionId = {}, editingItemId } = payload || {}
      return {
        ...state,
        existingEditByQuestionId: {
          ...state.existingEditByQuestionId,
          ...existingEditByQuestionId,
        },
        ...(editingItemId !== undefined ? { editingItemId } : {}),
      }
    }
    case UPDATE_EXISTING_METADATA_EDIT: {
      const { questionId, patch = {} } = payload || {}
      if (!questionId) return state
      return {
        ...state,
        existingEditByQuestionId: {
          ...state.existingEditByQuestionId,
          [questionId]: {
            ...(state.existingEditByQuestionId?.[questionId] || {}),
            ...patch,
          },
        },
      }
    }
    case CLEAR_EXISTING_METADATA_EDIT:
      return {
        ...state,
        existingEditByQuestionId: {},
        editingItemId: null,
      }
    case RESET_BULK_STANDARDS_STATE:
      return {
        ...initialState,
      }
    default:
      return state
  }
}

const getSaveTelemetryCounts = (succeeded = [], itemIdMap = {}) => {
  const clonedItemsCount = Object.keys(itemIdMap || {}).length
  const ownedItemsCount = Math.max(succeeded.length - clonedItemsCount, 0)
  return { clonedItemsCount, ownedItemsCount }
}

// sagas
function* runBatchSaga({
  batch,
  subject,
  grade,
  curriculumId,
  selectedRubric = null,
}) {
  const batchItemIds = uniq(batch.map((q) => q.itemId))
  const questionById = batch.reduce((acc, entry) => {
    if (entry?.questionId) acc[entry.questionId] = entry.question
    return acc
  }, {})

  try {
    const results = yield call(aiAPI.generateQuestionMetadataBulk, {
      questions: batch.map(({ questionId, payload }) => ({
        questionId,
        ...payload,
      })),
      subject,
      ...(grade ? { grades: [grade] } : {}),
      ...(curriculumId ? { curriculumId } : {}),
    })

    const suggestions = {}
    ;(results || []).forEach((r) => {
      if (!r?.questionId) return
      suggestions[r.questionId] = withSelectedRubricOnSuggestion({
        suggestion: {
          standard: r.standard || null,
          dok: r.dok ?? null,
          difficulty: r.difficulty ?? null,
          confidence_score: r.confidence_score ?? null,
        },
        question: questionById[r.questionId],
        selectedRubric,
      })
    })

    yield put(setBulkSuggestionsAction(suggestions))
  } catch (err) {
    yield put(
      setBulkItemStatusAction({
        itemIds: batchItemIds,
        status: BULK_ITEM_STATUS.ERROR,
      })
    )
  }
}

export function* generateBulkStandardsSaga({ payload }) {
  const { grade = null, subject = null, curriculumId = null, rubricId = null } =
    payload || {}

  if (!subject) {
    notification({
      type: 'warning',
      msg: 'Please select a standard set to generate metadata',
    })
    return
  }

  try {
    const testItems = yield select(getTestItemsSelector)
    const userId = yield select(getUserIdSelector)
    const userCollections = yield select(getCollectionsSelector)
    const writableCollections = yield select(getWritableCollectionsSelector)
    const userRole = yield select(getUserRole)
    const isCurator = yield select(getIsCurator)
    const eligible = getEligibleTestMetadataQuestions({
      testItems,
      userId,
      userCollections,
      writableCollections,
      userRole,
      isCurator,
    })

    if (!eligible.length) {
      notification({
        type: 'info',
        msg: 'No eligible questions are missing metadata',
      })
      yield put(resetBulkStandardsStateAction())
      return
    }

    const affectedItemIds = uniq(eligible.map((q) => q.itemId))
    const initialItemStatus = affectedItemIds.reduce((acc, id) => {
      acc[id] = BULK_ITEM_STATUS.GENERATING
      return acc
    }, {})

    yield put(
      setBulkStandardsStateAction({
        apiStatus: 'INITIATED',
        selectedGrade: grade,
        selectedSubject: subject,
        selectedCurriculumId: curriculumId,
        itemStatus: initialItemStatus,
        suggestionsByQuestionId: {},
        errorMessage: null,
      })
    )

    // Resolve once, stamp onto essay suggestions when each batch response arrives.
    let selectedRubric = null
    if (rubricId) {
      try {
        const resolved = yield call(resolveRubricForApply, rubricId)
        selectedRubric = {
          metadata: resolved.metadata,
          maxScore: resolved.maxScore,
        }
      } catch (err) {
        notification({
          type: 'warning',
          msg:
            err?.code === 'RUBRIC_CRITERIA_MISSING'
              ? 'Selected rubric has no criteria and cannot be applied.'
              : 'Unable to apply the selected rubric after generating standards.',
        })
      }
    }

    const batches = chunk(eligible, BULK_METADATA_BATCH_SIZE)
    yield all(
      batches.map((batch) =>
        call(runBatchSaga, {
          batch,
          subject,
          grade,
          curriculumId,
          selectedRubric,
        })
      )
    )

    if (selectedRubric) {
      const suggestionsByQuestionId = yield select(getBulkSuggestionsSelector)
      const appliedCount = Object.values(suggestionsByQuestionId || {}).filter(
        (s) => s?.rubricApplied
      ).length
      if (appliedCount) {
        segmentApi.genericEventTrack(
          'rubric_session_applied',
          {
            applied_count: appliedCount,
            rubric_id: selectedRubric.metadata?._id || rubricId,
          },
          { attachUserData: true }
        )
      }
    }

    const eligibleQuestionIdsByItemId = eligible.reduce(
      (acc, { itemId, questionId }) => {
        if (!acc[itemId]) acc[itemId] = new Set()
        acc[itemId].add(questionId)
        return acc
      },
      {}
    )

    const { itemStatus, suggestionsByQuestionId } = yield select(
      getBulkStandardsStateSelector
    )

    const successItemIds = []
    const incompleteItemIds = []

    Object.entries(eligibleQuestionIdsByItemId).forEach(
      ([itemId, questionIds]) => {
        if (itemStatus[itemId] === BULK_ITEM_STATUS.ERROR) return

        const allComplete = [...questionIds].every(
          (qId) => suggestionsByQuestionId[qId] !== undefined
        )

        if (allComplete) {
          successItemIds.push(itemId)
        } else {
          incompleteItemIds.push(itemId)
        }
      }
    )

    if (successItemIds.length) {
      yield put(
        setBulkItemStatusAction({
          itemIds: successItemIds,
          status: BULK_ITEM_STATUS.SUCCESS,
        })
      )
    }

    if (incompleteItemIds.length) {
      yield put(
        setBulkItemStatusAction({
          itemIds: incompleteItemIds,
          status: BULK_ITEM_STATUS.ERROR,
        })
      )
    }

    yield put(setBulkStandardsStateAction({ apiStatus: 'SUCCESS' }))
  } catch (err) {
    yield put(
      setBulkStandardsStateAction({
        apiStatus: 'FAILED',
        errorMessage: 'Failed to generate metadata suggestions',
      })
    )
  }
}

export function* initExistingMetadataEditSaga({ payload }) {
  const { itemId, questionIds = [] } = payload || {}
  if (!itemId || !questionIds.length) return

  const test = yield select(getTestEntitySelector)
  const item = findItemOnTest(test, itemId)
  if (!item) return

  const questions = get(item, 'data.questions', [])
  const standardsDescriptions = get(test, 'summary.standardsDescriptions', [])
  const existingEditByQuestionId = questionIds.reduce((acc, qId) => {
    const question = questions.find((q) => q.id === qId)
    if (question) {
      acc[qId] = buildQuestionFromPersistedFields(
        question,
        standardsDescriptions
      )
    }
    return acc
  }, {})

  yield put(
    setExistingMetadataEditAction({
      existingEditByQuestionId,
      editingItemId: itemId,
    })
  )
}

export function* saveItemStandardSaga({ payload }) {
  const { itemId, questionIds = [], isExistingQuestionEdit = false } =
    payload || {}

  try {
    const test = yield select(getTestEntitySelector)
    const item = findItemOnTest(test, itemId)
    const itemQuestionIds = item ? getItemQuestionIds(item) : questionIds

    if (test?.status !== statusConstants.DRAFT) {
      if (isExistingQuestionEdit) {
        yield put(clearExistingMetadataEditAction())
      } else {
        yield put(clearBulkItemAction({ itemId, questionIds, itemQuestionIds }))
      }
      return
    }

    const allCurriculums = yield select(getCurriculumsListSelector)
    const existingEditByQuestionId = yield select(
      getExistingEditByQuestionIdSelector
    )
    const itemDataSource = isExistingQuestionEdit
      ? existingEditByQuestionId
      : yield select(getBulkSuggestionsSelector)

    const itemSuggestions = questionIds.reduce((acc, qId) => {
      if (itemDataSource[qId]) acc[qId] = itemDataSource[qId]
      return acc
    }, {})

    if (!item) {
      if (isExistingQuestionEdit) {
        yield put(clearExistingMetadataEditAction())
      } else {
        yield put(clearBulkItemAction({ itemId, questionIds, itemQuestionIds }))
      }
      return
    }

    const questions = get(item, 'data.questions', [])
    const updatedQuestions = questions.map((q) => {
      const editData = itemSuggestions[q.id]
      if (!editData) return q
      return buildQuestionForBulkSave(q, editData, allCurriculums, {
        isExistingQuestionEdit,
      })
    })

    const itemToSave = buildUpdatedItemForSave(item, updatedQuestions)
    const cloneNonOwnedItems = !isExistingQuestionEdit

    yield put(
      setBulkItemStatusAction({
        itemIds: [itemId],
        status: BULK_ITEM_STATUS.SAVING,
      })
    )

    const { succeeded = [], failed = [], itemIdMap = {} } = yield call(
      testItemsApi.bulkUpdate,
      {
        testId: test._id,
        cloneNonOwnedItems,
        items: [{ itemId, item: itemToSave }],
      }
    )

    const failedIds = new Set((failed || []).map((f) => f.id))
    if (failedIds.has(itemId)) {
      yield put(
        setBulkItemStatusAction({
          itemIds: [itemId],
          status: BULK_ITEM_STATUS.SUCCESS,
        })
      )
      notification({
        type: 'error',
        msg: isExistingQuestionEdit
          ? 'Failed to save metadata'
          : 'Failed to save metadata suggestion',
      })
      return
    }

    yield put(
      removeCreatedItemsByIdAction(getSavedItemIds(succeeded, itemIdMap))
    )
    yield put(receiveTestByIdAction(test._id, true, false))
    yield take(RECEIVE_TEST_BY_ID_SUCCESS)
    const updatedTest = yield select(getTestEntitySelector)
    const remapItemIdMap = buildRemapItemIdMap({
      apiItemIdMap: itemIdMap,
      items: [{ itemId, questionIds }],
      updatedTest,
    })
    const { newItemId, questionIdMap } = remapItemIdMap[itemId] || {
      newItemId: itemIdMap[itemId] || itemId,
      questionIdMap: {},
    }
    const updatedItem = findItemOnTest(updatedTest, newItemId)
    const updatedQuestionIds = getUpdatedQuestionIds(questionIds, questionIdMap)
    const updatedItemQuestionIds = updatedItem
      ? getItemQuestionIds(updatedItem)
      : updatedQuestionIds

    yield put(remapBulkItemIdsAction({ itemIdMap: remapItemIdMap }))

    if (isExistingQuestionEdit) {
      yield put(clearExistingMetadataEditAction())
    } else {
      yield put(
        clearBulkItemAction({
          itemId: newItemId,
          questionIds: updatedQuestionIds,
          itemQuestionIds: updatedItemQuestionIds,
        })
      )
    }

    if (!isExistingQuestionEdit) {
      const { clonedItemsCount, ownedItemsCount } = getSaveTelemetryCounts(
        succeeded,
        itemIdMap
      )
      segmentApi.genericEventTrack(
        'AITestQuestionsMetadata:SaveSuggestionsClick',
        {
          testId: test._id,
          itemId,
          clonedItemsCount,
          ownedItemsCount,
        },
        { attachUserData: true }
      )
    }

    notification({
      type: 'success',
      msg: isExistingQuestionEdit
        ? 'Metadata saved successfully'
        : 'Suggestions saved successfully',
    })
  } catch (err) {
    yield put(
      setBulkItemStatusAction({
        itemIds: [itemId],
        status: BULK_ITEM_STATUS.SUCCESS,
      })
    )
    notification({
      type: 'error',
      msg: isExistingQuestionEdit
        ? 'Failed to save metadata'
        : 'Failed to save metadata suggestion',
    })
  }
}

export function* saveAllItemStandardsSaga() {
  const pending = yield select(getPendingSuggestionsSelector)
  if (!pending.length) return

  const affectedItemIds = pending.map((p) => p.itemId)
  try {
    const test = yield select(getTestEntitySelector)

    if (test?.status !== statusConstants.DRAFT) {
      yield put(resetBulkStandardsStateAction())
      return
    }

    const allCurriculums = yield select(getCurriculumsListSelector)
    const allSuggestions = yield select(getBulkSuggestionsSelector)

    const itemsById = (test.itemGroups || [])
      .flatMap((group) => group.items || [])
      .reduce((acc, it) => {
        if (it && typeof it !== 'string') acc[it._id] = it
        return acc
      }, {})

    const itemsToUpdate = pending
      .map(({ itemId, questionIds }) => {
        const item = itemsById[itemId]
        if (!item) return null
        const itemSuggestions = questionIds.reduce((acc, qId) => {
          if (allSuggestions[qId]) acc[qId] = allSuggestions[qId]
          return acc
        }, {})
        const questions = get(item, 'data.questions', [])
        const updatedQuestions = questions.map((q) =>
          buildQuestionForBulkSave(q, itemSuggestions[q.id], allCurriculums)
        )
        return {
          itemId,
          questionIds,
          itemQuestionIds: getItemQuestionIds(item),
          updatedItem: buildUpdatedItemForSave(item, updatedQuestions),
        }
      })
      .filter(Boolean)

    if (!itemsToUpdate.length) {
      yield put(resetBulkStandardsStateAction())
      return
    }

    yield put(
      setBulkItemStatusAction({
        itemIds: affectedItemIds,
        status: BULK_ITEM_STATUS.SAVING,
      })
    )

    const { succeeded = [], failed = [], itemIdMap = {} } = yield call(
      testItemsApi.bulkUpdate,
      {
        testId: test._id,
        cloneNonOwnedItems: true,
        items: itemsToUpdate.map(({ itemId, updatedItem }) => ({
          itemId,
          item: updatedItem,
        })),
      }
    )

    if (!succeeded.length) {
      throw new Error('Failed to save metadata suggestions')
    }

    const failedIds = new Set((failed || []).map((f) => f.id))
    const successfulItems = itemsToUpdate.filter(
      ({ itemId }) => !failedIds.has(itemId)
    )

    yield put(
      removeCreatedItemsByIdAction(getSavedItemIds(succeeded, itemIdMap))
    )
    yield put(receiveTestByIdAction(test._id, true, false))
    yield take(RECEIVE_TEST_BY_ID_SUCCESS)
    const updatedTest = yield select(getTestEntitySelector)
    const remapItemIdMap = buildRemapItemIdMap({
      apiItemIdMap: itemIdMap,
      items: successfulItems,
      updatedTest,
    })

    yield put(remapBulkItemIdsAction({ itemIdMap: remapItemIdMap }))

    for (const { itemId, questionIds } of successfulItems) {
      const { newItemId, questionIdMap } = remapItemIdMap[itemId] || {
        newItemId: itemIdMap[itemId] || itemId,
        questionIdMap: {},
      }
      const updatedItem = findItemOnTest(updatedTest, newItemId)
      const updatedQuestionIds = getUpdatedQuestionIds(
        questionIds,
        questionIdMap
      )
      const updatedItemQuestionIds = updatedItem
        ? getItemQuestionIds(updatedItem)
        : updatedQuestionIds

      yield put(
        clearBulkItemAction({
          itemId: newItemId,
          questionIds: updatedQuestionIds,
          itemQuestionIds: updatedItemQuestionIds,
        })
      )
    }

    if (failedIds.size) {
      yield put(
        setBulkItemStatusAction({
          itemIds: [...failedIds],
          status: BULK_ITEM_STATUS.ERROR,
        })
      )
    } else {
      yield put(resetBulkStandardsStateAction())
    }

    const { clonedItemsCount, ownedItemsCount } = getSaveTelemetryCounts(
      succeeded,
      itemIdMap
    )
    segmentApi.genericEventTrack(
      'AITestQuestionsMetadata:SaveAllSuggestionsClick',
      {
        testId: test._id,
        itemCount: itemsToUpdate.length,
        clonedItemsCount,
        ownedItemsCount,
        failedCount: failed.length,
      },
      { attachUserData: true }
    )

    if (!failedIds.size) {
      notification({
        type: 'success',
        msg: 'Suggestions saved successfully',
      })
    }
  } catch (err) {
    yield put(
      setBulkItemStatusAction({
        itemIds: affectedItemIds,
        status: BULK_ITEM_STATUS.SUCCESS,
      })
    )
    notification({
      type: 'error',
      msg: 'Failed to save metadata suggestions',
    })
  }
}

export function* watcherSaga() {
  yield all([
    takeLatest(GENERATE_BULK_STANDARDS, generateBulkStandardsSaga),
    takeEvery(SAVE_ITEM_STANDARD, saveItemStandardSaga),
    takeLatest(SAVE_ALL_ITEM_STANDARDS, saveAllItemStandardsSaga),
    takeLatest(INIT_EXISTING_METADATA_EDIT, initExistingMetadataEditSaga),
  ])
}
